import { Injectable } from '@angular/core';
import { ApiService } from '../../core/services/api/api.service';
import { EditProgramRequestBodyI } from './interfaces/edit-program-request-body.interface';
import { FilterProgramsI } from './interfaces/filter-programs.interface';
import { ProgramI } from './interfaces/program.interface';
import { PloI } from '../../shared/interfaces/plo.interface';
import { DeletePloRequestQuery } from '../../shared/interfaces/delete-plo-request-query.interface';
import { EditEvaluationCriterionRequestBodyI } from './interfaces/edit-evaluation-criterion-request-body.interface';
import { CreatePloDto } from './dtos/create-plo.dto';
import { AddProgramGoalRequestBodyI } from './programs-details/program-goals/interfaces/add-program-goal-request-body.interface';
import { ProgramGoalI } from './programs-details/program-goals/interfaces/program-goal.interface';
import { DeleteProgramGoalRequestQueryI } from './programs-details/program-goals/interfaces/delete-program-goal-request-query.interface';
import { FilterProgramGoalsQueryI } from './programs-details/program-goals/interfaces/filter-program-goal-query.interface';
import { UpdatePloDto } from './dtos/update-plo.dto';
import { EvaluationCriterionI } from './interfaces/evaluation-criterion.interface';
import { FilterAllProgramPlosDto } from './dtos/find-all-program-plos.dto';
import { FindAllProgramCompetenciesDto } from './dtos/find-all-program-competencies.dto';
import { CompetenciesI } from '../../shared/interfaces/competency.interface';
import { FindProgramReportI } from './dtos/find-program-aol-report.dto';
import { CreateEvaluationCriterionRequestBodyI } from './interfaces/create-evaluation-criterion-request-body.interface';
import { CourseSectionI } from '../course-sections/interfaces/course-section.interface';
import { FilterCoursesCompletionsI } from './interfaces/filter-courses-completions.interface';
import { ProgramReportI } from './interfaces/program-report.interface';
import { CreateCompetencyDto } from './dtos/create-competency.dto';
import { FilterCourseSectionsOutlierI } from './interfaces/filter-course-sections-outlier.interface';
import { CourseSectionOutlierI } from './interfaces/course-section-outlier.interface';
import { FilterCompletionsCountDto } from './dtos/filter-completions-count.dto';
import { CourseI } from '../courses/interfaces/course.interface';

@Injectable({
  providedIn: 'root',
})
export class ProgramsApiService {
  constructor(private readonly apiService: ApiService) {}

  findAllForFaculty(facultyID: string, filterPrograms: FilterProgramsI) {
    return this.apiService.get<ProgramI[]>(
      `programs/faculties/${facultyID}`,
      filterPrograms
    );
  }

  findAll(filterPrograms: FilterProgramsI) {
    return this.apiService.get<ProgramI[]>('programs', filterPrograms);
  }

  findOne(programID: string) {
    return this.apiService.get<ProgramI>(`programs/${programID}`);
  }
  findStudentsCount(programID: string) {
    return this.apiService.get<number>(`students/programs/${programID}/count`);
  }
  findCoursesCount(programID: string) {
    return this.apiService.get<number>(`courses/programs/${programID}/count`);
  }

  createPlo(createPloDto: CreatePloDto, programID: string) {
    return this.apiService.post<PloI>(
      `plos/programs/${programID}`,
      createPloDto
    );
  }

  createCompetency(createCompetencyDto: CreateCompetencyDto) {
    return this.apiService.post<CompetenciesI>(
      `competencies`,
      createCompetencyDto
    );
  }

  updateCompetency(
    createCompetencyDto: CreateCompetencyDto,
    competencyID: string
  ) {
    return this.apiService.patch<CompetenciesI>(
      `competencies/${competencyID}`,
      createCompetencyDto
    );
  }

  editPlo(ploID: string, updatePloDto: UpdatePloDto) {
    return this.apiService.patch<PloI>(`plos/${ploID}`, updatePloDto);
  }

  deletePlo(ploID: string, deletePloRequestQuery: DeletePloRequestQuery) {
    return this.apiService.delete<PloI>(`plos/${ploID}`, deletePloRequestQuery);
  }

  removeCompetency(
    competencyID: string,
    DeleteCompetencyRequestQuery: DeletePloRequestQuery
  ) {
    return this.apiService.delete<CompetenciesI>(
      `competencies/${competencyID}`,
      DeleteCompetencyRequestQuery
    );
  }

  findAllForProgram(
    programID: string,
    findAllProgramPlosDto: FilterAllProgramPlosDto
  ) {
    return this.apiService.get<PloI[]>(
      `plos/programs/${programID}`,
      findAllProgramPlosDto
    );
  }

  findAllCompetencies(
    programID: string,
    findAllProgramCompetenciesDto: FindAllProgramCompetenciesDto
  ) {
    return this.apiService.get<CompetenciesI[]>(
      `competencies/programs/${programID}`,
      findAllProgramCompetenciesDto
    );
  }

  editEvaluationCriteria(
    editEvaluationCriterionRequestBody: EditEvaluationCriterionRequestBodyI,
    evaluationCriteriaID: string
  ) {
    return this.apiService.put(
      `evaluation-criteria/${evaluationCriteriaID}`,
      editEvaluationCriterionRequestBody
    );
  }
  createEvaluationCriteria(
    createEvaluationCriterionRequestBody: CreateEvaluationCriterionRequestBodyI
  ) {
    return this.apiService.post(
      `evaluation-criteria`,
      createEvaluationCriterionRequestBody
    );
  }
  createGlobalEvaluationCriteria(
    createEvaluationCriterionRequestBody: CreateEvaluationCriterionRequestBodyI
  ) {
    return this.apiService.post<EvaluationCriterionI>(
      `evaluation-criteria`,
      createEvaluationCriterionRequestBody
    );
  }

  report(programID: string, createProgramReport: FindProgramReportI) {
    return this.apiService.get<ProgramReportI>(
      `programs/${programID}/report`,
      createProgramReport
    );
  }

  createProgramGoal(
    programID: string,
    createProgramGoalRequestBody: AddProgramGoalRequestBodyI
  ) {
    return this.apiService.post<ProgramGoalI>(
      `program-goals/${programID}`,
      createProgramGoalRequestBody
    );
  }

  updateProgramGoal(
    goalID: string,
    editGoalRequestBody: EditProgramRequestBodyI
  ) {
    return this.apiService.patch<ProgramGoalI>(
      `program-goals/${goalID}`,
      editGoalRequestBody
    );
  }

  deleteProgramGoal(
    goalID: string,
    deleteGoalRequestQuery: DeleteProgramGoalRequestQueryI
  ) {
    return this.apiService.delete<ProgramGoalI>(
      `program-goals/${goalID}`,
      deleteGoalRequestQuery
    );
  }

  findAllGoals(
    goalID: string,
    filterProgramGoalsQuery: FilterProgramGoalsQueryI
  ) {
    return this.apiService.get<{
      programGoals: ProgramGoalI[];
      count: number;
    }>(`program-goals/programs/${goalID}`, filterProgramGoalsQuery);
  }

  findAllCourseSections(
    programID: string,
    filterCoursesCompletions: FilterCoursesCompletionsI
  ) {
    return this.apiService.get<CourseSectionI[]>(
      `course-sections/programs/${programID}`,
      filterCoursesCompletions
    );
  }
  findAllCoursesForProgram(
    programID: string,
    filterCoursesCompletions: FilterCoursesCompletionsI
  ) {
    return this.apiService.get<CourseI[]>(
      `courses?programsIDs=${programID}&usePrograms=false`,
      filterCoursesCompletions
    );
  }

  findCoursesForProgramCount(
    programID: string,
    filterCount?: FilterCompletionsCountDto
  ) {
    return this.apiService.get<number | 'zero'>(
      `courses/count?programsIDs=${programID}`,
      filterCount
    );
  }
  findCourseSectionsCount(
    programID: string,
    filterCount?: FilterCompletionsCountDto
  ) {
    return this.apiService.get<number | 'zero'>(
      `course-sections/programs/${programID}/count`,
      filterCount
    );
  }

  findEvaluationCriterionForProgram(programID: string) {
    return this.apiService.get<EvaluationCriterionI>(
      `evaluation-criteria/programs/${programID}`
    );
  }
  findCourseSectionsOutliers(
    filterCourseSectionsOutliers: FilterCourseSectionsOutlierI
  ) {
    return this.apiService.get<CourseSectionOutlierI[]>(
      `course-sections/outlier`,
      filterCourseSectionsOutliers
    );
  }
}
